import { inject, Injectable } from '@angular/core';

import { ROLE_LABELS, toStarkIcon } from '../../config';
import type { UserRole } from '../../interfaces/tool.interface';
import { ToolRegistryService } from '../firestore/tool-registry.service';
import { LocaleService } from '../i18n/locale.service';
import { translateNow } from '../i18n/translate-now';

/**
 * Sidebar item interface for UI consumption
 */
interface SidebarItem {
  id: string;
  label?: string;
  icon?: string;
  type: 'section' | 'divider' | 'action' | 'item';
  badge?: number;
  danger?: boolean;
  route?: string;
}

/**
 * Tool pinned to the sidebar from code, independent of the category it lives in (the
 * configurator can move it freely). The tool viewer resolves tools by id and ignores the
 * category segment, so the fixed route keeps working wherever the tool is placed.
 */
export const PINNED_SIDEBAR_TOOL = {
  id: 'process-records',
  label: 'Registro de Peticiones',
  icon: 'history',
  route: '/support/process-records',
} as const;

@Injectable({ providedIn: 'root' })
export class SidebarItemsBuilderService {
  private readonly toolRegistry = inject(ToolRegistryService);
  private readonly localeService = inject(LocaleService);

  /**
   * Build sidebar items from ToolRegistryService categories and tools.
   * Groups items into sections by category, with favorites at the top.
   *
   * Filtering logic:
   * - Only includes tools with is_active === true
   * - Only includes the tools the registry already filtered for the caller's Auth0 roles
   * - Favorite tools are shown at the top in a dedicated section
   * - Remaining tools are grouped by their category
   *
   * Reads the active language, so a `computed()` that calls it is rebuilt when the language
   * changes: the section and pinned labels are built in code, not in a template.
   *
   * @param userRole - User role for access filtering
   * @param favoriteIds - Array of favorite tool IDs
   * @returns SidebarItem[] with sections and actions
   */
  getItems(userRole: UserRole, favoriteIds: string[]): SidebarItem[] {
    this.localeService.locale(); // dependency read, see the TSDoc above
    const items: SidebarItem[] = [];
    const categories = this.toolRegistry.categories();

    items.push({
      id: PINNED_SIDEBAR_TOOL.id,
      label: translateNow('layout.sidebar.requestLog'),
      icon: toStarkIcon(PINNED_SIDEBAR_TOOL.icon),
      type: 'action',
      route: PINNED_SIDEBAR_TOOL.route,
    });

    // Favoritos primero (items individuales de herramientas)
    if (favoriteIds.length > 0) {
      items.push({ id: 'section-fav', label: translateNow('layout.sidebar.favorites'), type: 'section' });
      const allTools = this.toolRegistry.allToolsById();
      favoriteIds.forEach(id => {
        const tool = allTools.get(id);
        if (tool) {
          items.push({
            id: tool.id,
            label: tool.name,
            icon: toStarkIcon(tool.icon ?? tool.icon_url),
            type: 'action',
            route: `/${id}`,
          });
        }
      });
    }

    // Cada CATEGORÍA como un item clickeable
    if (categories.length > 0) {
      items.push({ id: 'section-tools', label: translateNow('layout.sidebar.tools'), type: 'section' });
      categories.forEach(cat => {
        if (cat.tools.length > 0) {
          items.push({
            id: cat.id,
            label: cat.name,
            icon: toStarkIcon(cat.icon ?? cat.icon_url),
            type: 'action',
          });
        }
      });
    }

    return items;
  }

  /**
   * Get the route for a tool by searching all categories.
   * Fallback to '/' if tool not found.
   *
   * @param itemId - Tool ID
   * @returns Route path for the tool
   */
  getRoute(itemId: string): string {
    const categories = this.toolRegistry.categories();
    for (const category of categories) {
      const tool = category.tools.find(t => t.id === itemId);
      if (tool) {
        // For now, construct route from tool ID
        // In the future, this could come from ToolRegistryDoc.slate_doc_url or a similar field
        return `/${itemId}`;
      }
    }
    return '/';
  }

  /**
   * Extract initials from user name for avatar display.
   * Takes up to 2 initials (first character of first and second word).
   *
   * @param userName - Full user name
   * @returns Up to 2 uppercase initials
   */
  getInitials(userName: string): string {
    return userName
      .split(' ')
      .map(w => w[0] ?? '')
      .join('')
      .substring(0, 2)
      .toUpperCase();
  }

  /**
   * Get the human-readable label for a user role.
   *
   * @param role - User role
   * @returns Localized role label
   */
  getUserRoleLabel(role: UserRole): string {
    return ROLE_LABELS[role] ?? 'Unknown';
  }
}
